import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api/axios';
import styles from './Auth.module.css';

export const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const urlToken = searchParams.get('token');
    if (urlToken) {
      setToken(urlToken.toUpperCase());
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!token) {
      setError('Please enter a reset token');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post('/api/password-reset-token', {
        token: token.trim().toUpperCase(),
        new_password: newPassword,
        new_password_confirmation: confirmPassword,
      });
      
      setSuccess(true);
      
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.card} style={{ textAlign: 'center' }}>
          <div className={styles.successIcon}>✓</div>
          <h2 className={styles.successTitle}>Password Reset Successful!</h2>
          <p className={styles.successText}>Your password has been reset successfully.</p>
          <p className={styles.successHint}>Redirecting to login page in 3 seconds...</p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            className={styles.primaryButton}
          >
            Go to Login Now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <div className={styles.card}>
        <div className={styles.logoSection}>
          <div className={styles.logoIcon}>🔐</div>
          <h1 className={styles.logoTitle}>Reset Password</h1>
          <p className={styles.logoSubtitle}>
            {token ? 'Enter your new password below.' : 'Enter the reset token from your email and your new password.'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {!searchParams.get('token') && (
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Reset Token</label>
              <input
                type="text"
                value={token}
                onChange={(e) => setToken(e.target.value.toUpperCase())}
                placeholder="Enter 8-character token"
                maxLength={8}
                required
                autoFocus
                className={`${styles.formInput} ${styles.formInputToken}`}
              />
              <span className={styles.formHint}>Check your email for the 8-character reset token</span>
            </div>
          )}

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>New Password (min. 8 characters)</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              autoFocus={!!token}
              className={styles.formInput}
              placeholder="Enter new password"
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              className={styles.formInput}
              placeholder="Confirm new password"
            />
          </div>

          {error && (
            <div className={styles.errorMessage}>
              ⚠️ {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className={styles.primaryButton}
          >
            {isSubmitting ? 'Resetting Password...' : 'Reset Password'}
          </button>

          <button
            type="button"
            onClick={() => navigate('/login')}
            disabled={isSubmitting}
            className={styles.linkButton}
            style={{ textAlign: 'center', display: 'block', width: '100%' }}
          >
            ← Back to Login
          </button>
        </form>
      </div>
    </div>
  );
};
